import { useState, useEffect, useRef } from "react";
import { Link } from 'react-router-dom'
import { useURLState } from '../Utils/useURLState';
import { encodeStateToURL, updateURL } from '../Utils/urlStateUtils';
import Mono from "../Components/Colorize/ColorizeModes/Mono";
import Duo from "../Components/Colorize/ColorizeModes/Duo";
import Trio from "../Components/Colorize/ColorizeModes/Trio";
import chroma from "chroma-js";
import '../Styles/home.scss';
import '../Styles/Colorize/handleScreenSizes.scss';
import '../Styles/ReusableComponents/popupTimed.scss';
import Modules from "../Components/Modules";
import { getContrastRatio } from "../Components/Colorize/getTextColor";
import { useColorContext } from "../Components/Colorize/ColorContext";
import Header from "../Components/ReusableComponents/Header";
import Loader from "../Components/ReusableComponents/Loader";
import { usePopupQueue } from '../Components/ReusableComponents/usePopupQueue';
import { PopupDefaultSmall, CloseButton } from "../Components/ReusableComponents/PopupDefaultSmall";
import { PopupDefault } from "../Components/ReusableComponents/PopupDefault";
import Export from "../Components/Popups/Export";
import ExportToFigma from "../Components/Popups/ExportToFigma";
import ContrastGrid from "../Components/Popups/ContrastGrid";
import Share from "../Components/Popups/Share";
import { supabase } from '../supabase/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useMembershipGuard } from '../Components/Auth/useMembershipGuard';
import SkeletonButton from "../Components/ReusableComponents/SkeletonButton";


function Home({ darkMode }) {
  const { executeMembershipGuard } = useMembershipGuard();
  const { currentPaletteToView, clearPaletteToView, savePalette } = useColorContext();

  // Popup states 
  const [isExportVisible, setIsExportVisible] = useState(false);
  const [isContrastGridVisible, setIsContrastGridVisible] = useState(false);
  const [activePopupSource, setActivePopupSource] = useState(null); // 'mono', 'duo', or 'trio'
  const [isExportToFigmaVisible, setIsExportToFigmaVisible] = useState(false);
  const [isShareVisible, setIsShareVisible] = useState(false);

  const [isLoading, setIsLoading] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paletteId = urlParams.get('paletteId');
    const currentPath = window.location.pathname;
    const hasURLState = currentPath && currentPath !== '/' && currentPath !== '/generator';

    // Show loader for saved palettes OR URL state
    return !!(paletteId || currentPaletteToView || hasURLState);
  });

  const [isLoadingFromURL, setIsLoadingFromURL] = useState(false);

  

  const { user, isAuthReady, subscriptionActive } = useAuth();
  // const isAuthReady = !loading && !subscriptionLoading;

  // Popup handler functions
  const openExport = (source) => {
    setActivePopupSource(source);
    setIsExportVisible(true);
  };

  const closeExport = () => {
    setIsExportVisible(false);
    setActivePopupSource(null);
  };

  const openExportToFigma = (source) => {
    setActivePopupSource(source);
    setIsExportToFigmaVisible(true);
  };

  const closeExportToFigma = () => {
    setIsExportToFigmaVisible(false);
    setActivePopupSource(null);
  };

  const openContrastGrid = (source) => {
    setActivePopupSource(source);
    setIsContrastGridVisible(true);
  };

  const closeContrastGrid = () => {
    setIsContrastGridVisible(false);
    setActivePopupSource(null);
  };

  const openShare = (source) => {
    setIsShareVisible(true);
    setActivePopupSource(source);
  };

  const closeShare = () => {
    setIsShareVisible(false);
    setActivePopupSource(null);
  };

  // get the appropriate palette and color name based on active popup source
  const getActivePopupData = () => {
    switch (activePopupSource) {
      case 'duo':
        return { palette: paletteDuo, colorName: colorNameDuo, squareNames, selectedIndex: selectedIndexDuo };
      case 'trio':
        return { palette: paletteTrio, colorName: colorNameTrio, squareNames, selectedIndex: selectedIndexTrio };
      default:
        return { palette, colorName, squareNames, selectedIndex };
    }
  };

  // handle palettes
  const [palette, setPalette] = useState([]);
  const [paletteDuo, setPaletteDuo] = useState([]);
  const [paletteTrio, setPaletteTrio] = useState([]);

  const [color, setColor] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paletteId = urlParams.get('paletteId');

    // Check if we have URL state first
    const currentPath = window.location.pathname;

    // New on 3 nov
    const redirectPath = localStorage.getItem('redirectAfterLogin') ||
      sessionStorage.getItem('redirectAfterLogin');

    if (redirectPath) {
      const hexMatch = redirectPath.match(/\/generator\/([a-fA-F0-9]{6})/);
      if (hexMatch) {
        // Clean up the redirect path now that we've used it
        localStorage.removeItem('redirectAfterLogin');
        sessionStorage.removeItem('redirectAfterLogin');
        return `#${hexMatch[1]}`;
      }
    }
    // end of new on 3 nov



    const hasURLState = currentPath && currentPath !== '/' && currentPath !== '/generator';

    return (paletteId || currentPaletteToView || hasURLState) ? '' : generateRandomColor();
  });


  const [colorDuo, setColorDuo] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paletteId = urlParams.get('paletteId');
    const currentPath = window.location.pathname;

    // new 3 nov
    const redirectPath = localStorage.getItem('redirectAfterLogin') ||
      sessionStorage.getItem('redirectAfterLogin');

    if (redirectPath) {
      // Match Duo color: &D followed by 6 hex characters
      const duoMatch = redirectPath.match(/&D([a-fA-F0-9]{6})/);
      if (duoMatch) {
        return `#${duoMatch[1]}`;
      }
    }
    // end of new 3 nov



    const hasURLState = currentPath && currentPath !== '/' && currentPath !== '/generator';
    return (paletteId || currentPaletteToView || hasURLState) ? '' : generateRandomColorDuo();
  });

  const [colorTrio, setColorTrio] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paletteId = urlParams.get('paletteId');
    const currentPath = window.location.pathname;

    // new 3 nov
    const redirectPath = localStorage.getItem('redirectAfterLogin') ||
      sessionStorage.getItem('redirectAfterLogin');

    if (redirectPath) {
      // Match Trio color: &T followed by 6 hex characters
      const trioMatch = redirectPath.match(/&T([a-fA-F0-9]{6})/);
      if (trioMatch) {
        return `#${trioMatch[1]}`;
      }
    }
    // end of new 3 nov


    const hasURLState = currentPath && currentPath !== '/' && currentPath !== '/generator';
    return (paletteId || currentPaletteToView || hasURLState) ? '' : generateRandomColorTrio();
  });


  const [showDuo, setShowDuo] = useState(false);
  const [showTrio, setShowTrio] = useState(false);

  // handle colorize view and button
  const [isOutOfView, setIsOutOfView] = useState(false);
  const colorizeContainerRef = useRef(null);

  const innerColorizeRef = useRef(null);

  const [showSquares, setShowSquares] = useState(false)
  // handle selected option
  const [selectedOption, setSelectedOption] = useState("mono");

  // needed for moving squares on top of modules
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedIndexDuo, setSelectedIndexDuo] = useState(null);
  const [selectedIndexTrio, setSelectedIndexTrio] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [editIndexDuo, setEditIndexDuo] = useState(null);
  const [editIndexTrio, setEditIndexTrio] = useState(null);
  const [paletteModifiers, setPaletteModifiers] = useState({
    scale: {
      hue: 0,
      lightnessLeft: 0,
      lightnessRight: 0,
      saturationLeft: 0,
      saturationRight: 0
    },
    individual: {}
  });
  const [paletteModifiersDuo, setPaletteModifiersDuo] = useState({
    scale: {
      hue: 0,
      lightnessLeft: 0,
      lightnessRight: 0,
      saturationLeft: 0,
      saturationRight: 0
    },
    individual: {}
  });
  const [paletteModifiersTrio, setPaletteModifiersTrio] = useState({
    scale: {
      hue: 0,
      lightnessLeft: 0,
      lightnessRight: 0,
      saturationLeft: 0,
      saturationRight: 0
    },
    individual: {}
  });

  const [colorName, setColorName] = useState('');
  const [colorNameDuo, setColorNameDuo] = useState('');
  const [colorNameTrio, setColorNameTrio] = useState('');

  const [tailwindFamily, setTailwindFamily] = useState(null);
  const [tailwindFamilyDuo, setTailwindFamilyDuo] = useState(null);
  const [tailwindFamilyTrio, setTailwindFamilyTrio] = useState(null);

  const [confirmRemovePopupDuo, setConfirmRemovePopupDuo] = useState(false);
  const [confirmRemovePopupTrio, setConfirmRemovePopupTrio] = useState(false);

  const [squareNames, setSquareNames] = useState(["50", "100", "200", "300", "400", "500", "600", "700", "800", "900", "950"]);
  // URL state management
  const paletteState = {
    color,
    colorDuo,
    colorTrio,
    showDuo,
    showTrio,
    paletteModifiers,
    paletteModifiersDuo,
    paletteModifiersTrio,
    squareNames,
    tailwindFamily: null,      // ADD THESE
    tailwindFamilyDuo: null,   // ADD THESE
    tailwindFamilyTrio: null   // ADD THESE
  };

  const setPaletteState = (newState) => {
    if (newState.color !== undefined) {
      setColor(newState.color);
    }
    if (newState.colorDuo !== undefined) {
      setColorDuo(newState.colorDuo);
    }
    if (newState.colorTrio !== undefined) {
      setColorTrio(newState.colorTrio);
    }
    if (newState.showDuo !== undefined) {
      setShowDuo(newState.showDuo);
    }
    if (newState.showTrio !== undefined) {
      setShowTrio(newState.showTrio);
    }
    if (newState.paletteModifiers !== undefined) {
      setPaletteModifiers(newState.paletteModifiers);
    }
    if (newState.paletteModifiersDuo !== undefined) {
      setPaletteModifiersDuo(newState.paletteModifiersDuo);
    }
    if (newState.paletteModifiersTrio !== undefined) {
      setPaletteModifiersTrio(newState.paletteModifiersTrio);
    }
    if (newState.tailwindFamily !== undefined) {
      setTailwindFamily(newState.tailwindFamily);
    }
    if (newState.tailwindFamilyDuo !== undefined) {
      setTailwindFamilyDuo(newState.tailwindFamilyDuo);
    }
    if (newState.tailwindFamilyTrio !== undefined) {
      setTailwindFamilyTrio(newState.tailwindFamilyTrio);
    }
  };

  const { isUpdatingFromURL } = useURLState(paletteState, setPaletteState, squareNames);

  useEffect(() => {
    // Only load from URL state when actually updating from URL
    if (color && isUpdatingFromURL) {
      // Use a small delay to ensure ref is ready
      const timer = setTimeout(() => {
        if (monoRef.current?.loadFromURLState) {
          monoRef.current.loadFromURLState({
            color,
            paletteModifiers,
            tailwindFamily  // This can be null, that's okay
          });
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [color, paletteModifiers, tailwindFamily, isUpdatingFromURL]);

  useEffect(() => {
    // Load Duo from URL state when updating from URL
    if (colorDuo && duoRef.current && isUpdatingFromURL) {
      if (duoRef.current?.loadFromURLState) {
        duoRef.current.loadFromURLState({
          color: colorDuo,
          paletteModifiers: paletteModifiersDuo,
          tailwindFamilyDuo
        });
      }
    }
  }, [colorDuo, paletteModifiersDuo, tailwindFamilyDuo, isUpdatingFromURL]);

  useEffect(() => {
    // Load Trio from URL state when updating from URL
    if (colorTrio && trioRef.current && isUpdatingFromURL) {
      if (trioRef.current?.loadFromURLState) {
        trioRef.current.loadFromURLState({
          color: colorTrio,
          paletteModifiers: paletteModifiersTrio,
          tailwindFamilyTrio
        });
      }
    }
  }, [colorTrio, paletteModifiersTrio, tailwindFamilyTrio, isUpdatingFromURL]);

  useEffect(() => {
    // If we have URL state and color is loaded, we're not loading anymore
    if (color && window.location.pathname !== '/') {
      setIsLoading(false);
    }
  }, [color]);


  // rerender duo and trio when clicking 'add +'
  const [duoKey, setDuoKey] = useState(0);
  const [trioKey, setTrioKey] = useState(0);


  const monoRef = useRef(null);
  const duoRef = useRef(null);
  const trioRef = useRef(null);

  const [viewDashboard, setViewDashboard] = useState(false);

  // new popup queue
  const { activePopups, addPopup, updatePopup } = usePopupQueue();

  // load from url Part 1
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paletteId = urlParams.get('paletteId');
    // saved & regular
    if (paletteId) {
      setIsLoadingFromURL(true);
    }
  }, []);

  // load from url Part 2
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paletteId = urlParams.get('paletteId');

    // theme handling block:
    const themeId = urlParams.get('themeId');
    const mode = urlParams.get('mode');

    // for Themes
    if (themeId && !paletteId) {
      setIsLoading(true);
      setIsLoadingFromURL(true);
      window.history.replaceState({}, document.title, window.location.pathname);

      // Import and find the theme
      import('../Assets/JSON/themes.json').then(themesData => {
        const theme = themesData.themes.find(t => t.id === themeId);

        if (!theme) {
          console.error('Theme not found:', themeId);
          setIsLoading(false);
          setIsLoadingFromURL(false);
          return;
        }

        // Process theme based on mode
        const paletteToView = {
          name: theme.name,
          timestamp: Date.now(),
          palettes: {}
        };

        if (mode === 'mono') {
          paletteToView.palettes.mono = {
            ...theme.palettes[0],
            baseGeneratedPalette: theme.palettes[0].palette
          };
        } else if (mode === 'duo') {
          paletteToView.palettes.mono = {
            ...theme.palettes[0],
            baseGeneratedPalette: theme.palettes[0].palette
          };
          if (theme.palettes.length >= 2) {
            paletteToView.palettes.duo = {
              ...theme.palettes[1],
              baseGeneratedPalette: theme.palettes[1].palette
            };
          }
        } else if (mode === 'trio') {
          paletteToView.palettes.mono = {
            ...theme.palettes[0],
            baseGeneratedPalette: theme.palettes[0].palette
          };
          if (theme.palettes.length >= 2) {
            paletteToView.palettes.duo = {
              ...theme.palettes[1],
              baseGeneratedPalette: theme.palettes[1].palette
            };
          }
          if (theme.palettes.length >= 3) {
            paletteToView.palettes.trio = {
              ...theme.palettes[2],
              baseGeneratedPalette: theme.palettes[2].palette
            };
          }
        }

        // process the paletteToView (copy from your paletteId logic)
        const { palettes } = paletteToView;

        // fetermine which palette to load as primary
        let primaryPalette, secondaryPalette, tertiaryPalette;
        if (palettes.mono && (palettes.duo || palettes.trio)) {
          primaryPalette = palettes.mono;
          secondaryPalette = palettes.duo;
          tertiaryPalette = palettes.trio;
        } else if (palettes.trio) {
          primaryPalette = palettes.trio;
        } else if (palettes.duo) {
          primaryPalette = palettes.duo;
        } else {
          primaryPalette = palettes.mono;
        }

        // Set UI state
        if (tertiaryPalette) {
          setShowDuo(true);
          setShowTrio(true);
          setSelectedOption('trio');
        } else if (secondaryPalette) {
          setShowDuo(true);
          setShowTrio(false);
          setSelectedOption('duo');
        } else {
          setShowDuo(false);
          setShowTrio(false);
          setSelectedOption('mono');
        }

        // Set primary palette data
        if (primaryPalette) {
          setColor(primaryPalette.color);
          setPalette(primaryPalette.palette);
          setPaletteModifiers(primaryPalette.paletteModifiers || {
            scale: { hue: 0, lightnessLeft: 0, lightnessRight: 0, saturationLeft: 0, saturationRight: 0 },
            individual: {}
          });
          setSelectedIndex(primaryPalette.selectedIndex);
          setColorName(primaryPalette.colorName || '');
        }

        // Set secondary palette data
        if (secondaryPalette) {
          setColorDuo(secondaryPalette.color);
          setPaletteDuo(secondaryPalette.palette);
          setPaletteModifiersDuo(secondaryPalette.paletteModifiers || {
            scale: { hue: 0, lightnessLeft: 0, lightnessRight: 0, saturationLeft: 0, saturationRight: 0 },
            individual: {}
          });
          setSelectedIndexDuo(secondaryPalette.selectedIndex);
          setColorNameDuo(secondaryPalette.colorName || '');
        }

        // Set tertiary palette data
        if (tertiaryPalette) {
          setColorTrio(tertiaryPalette.color);
          setPaletteTrio(tertiaryPalette.palette);
          setPaletteModifiersTrio(tertiaryPalette.paletteModifiers || {
            scale: { hue: 0, lightnessLeft: 0, lightnessRight: 0, saturationLeft: 0, saturationRight: 0 },
            individual: {}
          });
          setSelectedIndexTrio(tertiaryPalette.selectedIndex);
          setColorNameTrio(tertiaryPalette.colorName || '');
        }

        // Load refs
        setTimeout(() => {
          const loadRefs = () => {
            if (primaryPalette && monoRef.current?.loadSavedPalette) {
              monoRef.current.loadSavedPalette({
                ...primaryPalette,
                skipRegeneration: true,
                skipPaletteRegeneration: true
              });
            }

            if (secondaryPalette && duoRef.current?.loadSavedPalette) {
              duoRef.current.loadSavedPalette({
                ...secondaryPalette,
                skipRegeneration: true,
                skipPaletteRegeneration: true
              });
            }

            if (tertiaryPalette && trioRef.current?.loadSavedPalette) {
              trioRef.current.loadSavedPalette({
                ...tertiaryPalette,
                skipRegeneration: true,
                skipPaletteRegeneration: true
              });
            }

            setIsLoading(false);
            setIsLoadingFromURL(false);
          };

          if (monoRef.current?.loadSavedPalette) {
            loadRefs();
          } else {
            setTimeout(loadRefs, 100);
          }
        }, 100);

      }).catch(error => {
        console.error('❌ Error importing themes:', error);
        setIsLoading(false);
        setIsLoadingFromURL(false);
      });

      return;
    }


    // including for Saved items
    if (paletteId && user) {
      setIsLoading(true);
      setIsLoadingFromURL(true);

      const fetchPaletteFromSupabase = async () => {
        try {
          const { data, error } = await supabase
            .from('color_palettes')
            .select('*')
            .eq('id', paletteId)
            .eq('user_id', user.id)
            .single();

          if (error || !data) {
            console.error('❌ Error fetching palette:', error);
            setIsLoading(false);
            setIsLoadingFromURL(false);
            return;
          }

          const paletteData = data.palette_data;

          const { palettes } = paletteData;

          // Your existing palette determination logic...
          let primaryPalette, secondaryPalette, tertiaryPalette;
          if (palettes.mono && (palettes.duo || palettes.trio)) {
            primaryPalette = palettes.mono;
            secondaryPalette = palettes.duo;
            tertiaryPalette = palettes.trio;
          } else if (palettes.trio) {
            primaryPalette = palettes.trio;
          } else if (palettes.duo) {
            primaryPalette = palettes.duo;
          } else {
            primaryPalette = palettes.mono;
          }

          // Set UI state FIRST
          if (tertiaryPalette) {
            setShowDuo(true);
            setShowTrio(true);
            setSelectedOption('trio');
          } else if (secondaryPalette) {
            setShowDuo(true);
            setShowTrio(false);
            setSelectedOption('duo');
          } else {
            setShowDuo(false);
            setShowTrio(false);
            setSelectedOption('mono');
          }

          // Set primary palette data
          if (primaryPalette) {
            setColor(primaryPalette.color);
            setPalette(primaryPalette.palette);
            setPaletteModifiers(primaryPalette.paletteModifiers);
            setSelectedIndex(primaryPalette.selectedIndex);
            setColorName(primaryPalette.colorName || '');
          }

          // Set secondary palette data
          if (secondaryPalette) {
            setColorDuo(secondaryPalette.color);
            setPaletteDuo(secondaryPalette.palette);
            setPaletteModifiersDuo(secondaryPalette.paletteModifiers);
            setSelectedIndexDuo(secondaryPalette.selectedIndex);
            setColorNameDuo(secondaryPalette.colorName || '');
          }

          // Set tertiary palette data
          if (tertiaryPalette) {
            setColorTrio(tertiaryPalette.color);
            setPaletteTrio(tertiaryPalette.palette);
            setPaletteModifiersTrio(tertiaryPalette.paletteModifiers);
            setSelectedIndexTrio(tertiaryPalette.selectedIndex);
            setColorNameTrio(tertiaryPalette.colorName || '');
          }

          // Wait for React to process all state updates
          setTimeout(() => {

            const loadRefs = () => {

              if (primaryPalette && monoRef.current?.loadSavedPalette) {
                monoRef.current.loadSavedPalette({
                  ...primaryPalette,
                  skipRegeneration: true,
                  skipPaletteRegeneration: true
                });
              }

              if (secondaryPalette && duoRef.current?.loadSavedPalette) {
                duoRef.current.loadSavedPalette({
                  ...secondaryPalette,
                  skipRegeneration: true,
                  skipPaletteRegeneration: true
                });
              }

              if (tertiaryPalette && trioRef.current?.loadSavedPalette) {
                trioRef.current.loadSavedPalette({
                  ...tertiaryPalette,
                  skipRegeneration: true,
                  skipPaletteRegeneration: true
                });
              }

              setIsLoading(false);
              setIsLoadingFromURL(false);

              // Clear URL after everything is loaded
              window.history.replaceState({}, document.title, window.location.pathname);
            };

            if (monoRef.current?.loadSavedPalette) {
              loadRefs();
            } else {
              setTimeout(loadRefs, 100);
            }
          }, 100);

        } catch (error) {
          console.error('💥 Error in fetch function:', error);
          setIsLoading(false);
          setIsLoadingFromURL(false);
        }
      };

      fetchPaletteFromSupabase();
    }
  }, [user]);


  // making sure 'fake container' takes the height from 'colorize container
  // Adjust height of fake-colorize--container dynamically
  useEffect(() => {
    const updateHeight = () => {
      if (colorizeContainerRef.current && innerColorizeRef.current) {
        colorizeContainerRef.current.style.height = `${innerColorizeRef.current.offsetHeight}px`;
      }
    };

    updateHeight(); // Set initial height

    const observer = new ResizeObserver(updateHeight);
    if (innerColorizeRef.current) observer.observe(innerColorizeRef.current);

    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
      observer.disconnect();
    };
  }, [showSquares, showDuo, showTrio, palette, paletteDuo, paletteTrio]);

  function generateRandomColor() {
    return `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0")}`;
  }
  function generateRandomColorDuo() {
    return `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0")}`;
  }
  function generateRandomColorTrio() {
    return `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0")}`;
  }

  // handle viewing colorizing
  useEffect(() => {
    // Track whether observer has been set up
    let observerSetup = false;
    let scrollListenerAdded = false;
    let observer = null;

    const handleScroll = () => {
      if (!colorizeContainerRef.current) return;
      const rect = colorizeContainerRef.current.getBoundingClientRect();

      if (rect.top >= 0) {
        setIsOutOfView(false);
        setShowSquares(false);

        window.removeEventListener("scroll", handleScroll);
        scrollListenerAdded = false;
      }
    };

    // Setup the observer function we can call multiple times if needed
    const setupObserver = () => {
      if (observerSetup || !colorizeContainerRef.current) return;

      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) {
            setIsOutOfView(true);

            if (scrollListenerAdded) {
              window.removeEventListener("scroll", handleScroll);
              scrollListenerAdded = false;
            }
          } else {
            setIsOutOfView(false);

            if (!scrollListenerAdded) {
              window.addEventListener("scroll", handleScroll);
              scrollListenerAdded = true;
            }
          }
        },
        { threshold: 0 }
      );

      observer.observe(colorizeContainerRef.current);
      observerSetup = true;
    };

    // Try to setup observer immediately
    setupObserver();

    // If it fails, try again after a delay
    if (!observerSetup) {
      const timerId = setTimeout(() => {
        setupObserver();

        // If still not setup, try every 100ms until loaded
        if (!observerSetup) {
          const intervalId = setInterval(() => {
            if (colorizeContainerRef.current && !observerSetup) {
              setupObserver();
            }

            // Once setup or after 3 seconds max, clear interval
            if (observerSetup || !isLoading) {
              clearInterval(intervalId);
            }
          }, 100);

          // Cleanup interval after 3 seconds maximum
          setTimeout(() => clearInterval(intervalId), 3000);
        }
      }, 200);

      return () => {
        clearTimeout(timerId);
        if (observer) {
          observer.disconnect();
        }
        window.removeEventListener("scroll", handleScroll);
      };
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paletteId = urlParams.get('paletteId');

    const currentPath = window.location.pathname;
    const hasURLState = currentPath && currentPath !== '/' && currentPath !== '/generator';

    // Only process context palette if no URL palette ID exists AND no URL state
    if (!paletteId && !isLoadingFromURL && !hasURLState && currentPaletteToView) {
      // Set loading state to prevent flashing
      setIsLoading(true);

      const { palettes } = currentPaletteToView;

      // Determine which palette to load as primary
      let primaryPalette, secondaryPalette, tertiaryPalette;

      if (palettes.mono && (palettes.duo || palettes.trio)) {
        // Multiple palettes saved - use as intended
        primaryPalette = palettes.mono;
        secondaryPalette = palettes.duo;
        tertiaryPalette = palettes.trio;
      } else if (palettes.trio) {
        // Only trio saved - load as primary
        primaryPalette = palettes.trio;
        secondaryPalette = null;
        tertiaryPalette = null;
      } else if (palettes.duo) {
        // Only duo saved - load as primary
        primaryPalette = palettes.duo;
        secondaryPalette = null;
        tertiaryPalette = null;
      } else {
        // Only mono saved
        primaryPalette = palettes.mono;
        secondaryPalette = null;
        tertiaryPalette = null;
      }

      // Set UI state based on what we're actually showing
      if (tertiaryPalette) {
        setShowDuo(true);
        setShowTrio(true);
        setSelectedOption('trio');
      } else if (secondaryPalette) {
        setShowDuo(true);
        setShowTrio(false);
        setSelectedOption('duo');
      } else {
        setShowDuo(false);
        setShowTrio(false);
        setSelectedOption('mono');
      }

      // Always load primary palette data
      if (primaryPalette) {
        setColor(primaryPalette.color);
        setPalette(primaryPalette.palette);
        setPaletteModifiers(primaryPalette.paletteModifiers);
        setSelectedIndex(primaryPalette.selectedIndex);
        setColorName(primaryPalette.colorName || '');
      }

      // Load secondary if it exists
      if (secondaryPalette) {
        setColorDuo(secondaryPalette.color);
        setPaletteDuo(secondaryPalette.palette);
        setPaletteModifiersDuo(secondaryPalette.paletteModifiers);
        setSelectedIndexDuo(secondaryPalette.selectedIndex);
        setColorNameDuo(secondaryPalette.colorName || '');
      }

      // Load tertiary if it exists
      if (tertiaryPalette) {
        setColorTrio(tertiaryPalette.color);
        setPaletteTrio(tertiaryPalette.palette);
        setPaletteModifiersTrio(tertiaryPalette.paletteModifiers);
        setSelectedIndexTrio(tertiaryPalette.selectedIndex);
        setColorNameTrio(tertiaryPalette.colorName || '');
      }

      function waitForRefReady(ref, callback, label = 'ref') {
        let attempts = 0;
        const maxAttempts = 60;

        function check() {
          if (ref.current?.loadSavedPalette) {
            callback();
          } else {
            attempts++;
            if (attempts < maxAttempts) {
              requestAnimationFrame(check);
            } else {
              console.warn(`[Home] ${label} not ready after ${maxAttempts} frames`);
            }
          }
        }

        requestAnimationFrame(check);
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Always load primary (which might be a remapped duo/trio)
          if (primaryPalette) {
            waitForRefReady(monoRef, () => {
              monoRef.current.loadSavedPalette({
                ...primaryPalette,
                skipRegeneration: true
              });
            }, 'monoRef');
          }

          // Only load duo/trio refs if they actually exist as secondary/tertiary
          if (secondaryPalette) {
            waitForRefReady(duoRef, () => {
              duoRef.current.loadSavedPalette({
                ...secondaryPalette,
                skipRegeneration: true
              });
            }, 'duoRef');
          }

          if (tertiaryPalette) {
            waitForRefReady(trioRef, () => {
              trioRef.current.loadSavedPalette({
                ...tertiaryPalette,
                skipRegeneration: true
              });
            }, 'trioRef');
          }

          clearPaletteToView();
          setIsLoading(false);
        });
      });
    }
  }, [currentPaletteToView, isUpdatingFromURL]);



  // Add a useEffect that runs when loading is complete
  useEffect(() => {
    if (!isLoading && colorizeContainerRef.current) {
      // Check if container is out of view after loading
      const rect = colorizeContainerRef.current.getBoundingClientRect();
      // If top is above viewport or not visible, set to true
      setIsOutOfView(rect.top < 0);

      // Create a new observer if needed
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsOutOfView(!entry.isIntersecting);
        },
        { threshold: 0 }
      );

      observer.observe(colorizeContainerRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [isLoading]);


  // Making sure a color will show in the url when you enter the page
  // Initial URL update after component mounts
  useEffect(() => {
    if (color && !isLoadingFromURL && !isUpdatingFromURL) {
      const currentPath = window.location.pathname;
      const hasURLState = currentPath && currentPath !== '/' && currentPath !== '/generator';

      if (!hasURLState && window.location.search === '') {
        // Small delay to ensure all state is initialized
        setTimeout(() => {
          const newPath = encodeStateToURL({
            color,
            colorDuo,
            colorTrio,
            showDuo,
            showTrio,
            paletteModifiers,
            paletteModifiersDuo,
            paletteModifiersTrio,
            squareNames
          });
          updateURL(newPath, true);
        }, 100);
      }
    }
  }, []); // Empty dependency array - runs once on mount




  // limit for saving x amount of palettes
  const checkPaletteLimit = async () => {
    if (!user) return false;

    try {
      const { count, error } = await supabase
        .from('color_palettes')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error checking palette count:', error);
        return false;
      }

      // return count >= 500;
      return count >= 250;
    } catch (error) {
      console.error('Error checking palette limit:', error);
      return false;
    }
  };

  // saving global
  const handleSave = async () => {
    if (!user) {
      addPopup(<span>Please sign in to save palettes</span>, 4000, 'error');
      return;
    }

    // nienke new save: Show loading popup immediately, capture its id
    const popupId = addPopup(<span>Saving…</span>, 8000, 'save');

    const limitReached = await checkPaletteLimit();
    if (limitReached) {addPopup(<span>You've reached the maximum limit of 250 saved palettes. Please delete some palettes before saving new ones.</span>, 6000, 'error');
      return;
    }

    const paletteToSave = {
      name: `Palette ${new Date().toLocaleString()}`,
      timestamp: Date.now(),
      palettes: {
        mono: {
          color: color,
          colorName: colorName,
          palette: palette,
          squareNames: squareNames,
          paletteModifiers: paletteModifiers,
          baseGeneratedPalette: monoRef.current?.getBaseGeneratedPalette() || palette,
          selectedIndex: selectedIndex,
          tailwindFamily: monoRef.current?.getTailwindFamily() || null
        }
      }
    };

    // Add Duo if it's shown
    if (showDuo) {
      paletteToSave.palettes.duo = {
        color: colorDuo,
        colorName: colorNameDuo,
        palette: paletteDuo,
        squareNames: squareNames,
        paletteModifiers: paletteModifiersDuo,
        baseGeneratedPalette: duoRef.current?.getBaseGeneratedPalette() || paletteDuo,
        selectedIndex: selectedIndexDuo,
        tailwindFamily: duoRef.current?.getTailwindFamily() || null
      };
    }

    // Add Trio if it's shown
    if (showTrio) {
      paletteToSave.palettes.trio = {
        color: colorTrio,
        colorName: colorNameTrio,
        palette: paletteTrio,
        squareNames: squareNames,
        paletteModifiers: paletteModifiersTrio,
        baseGeneratedPalette: trioRef.current?.getBaseGeneratedPalette() || paletteTrio,
        selectedIndex: selectedIndexTrio,
        tailwindFamily: trioRef.current?.getTailwindFamily() || null
      };
    }

    savePalette(paletteToSave);

    updatePopup(
      popupId,
      <span>
        Saved "{colorName}"
        {showDuo ? ` & "${colorNameDuo}"` : ''}
        {showTrio ? ` & "${colorNameTrio}"` : ''}
        <Link to="/saved">
          Go to Saved
        </Link>
      </span>,
      4000,
      'save'
    );
  };

  const handleSaveMono = async () => {
    if (!user) {
      addPopup(
        <span>Please sign in to save palettes</span>,
        4000,
        'error'
      );
      return;
    }

    const popupId = addPopup(<span>Saving…</span>, 8000, 'save');

    const limitReached = await checkPaletteLimit();
    if (limitReached) {
      addPopup(
        <span>You've reached the maximum limit of 250 saved palettes. Please delete some palettes before saving new ones.</span>,
        6000,
        'error'
      );
      return;
    }

    // Create a palette object that includes only the mono palette
    const paletteToSave = {
      name: `Primary Scale ${new Date().toLocaleString()}`,
      timestamp: Date.now(),
      palettes: {
        mono: {
          color: color,
          colorName: colorName,
          palette: palette,
          squareNames: squareNames,
          paletteModifiers: paletteModifiers,
          baseGeneratedPalette: monoRef.current?.getBaseGeneratedPalette() || palette,
          selectedIndex: selectedIndex,
          tailwindFamily: monoRef.current?.getTailwindFamily() || null
        }
      }
    };

    savePalette(paletteToSave);

    updatePopup(
      popupId,
      <span>
        Saved "{colorName}"
        <Link to="/saved">
          Go to Saved
        </Link>
      </span>,
      4000,
      'save'
    );
  };

  const handleSaveDuo = async () => {
    if (!user) {
      addPopup(
        <span>Please sign in to save palettes</span>,
        4000,
        'error'
      );
      return;
    }

    const popupId = addPopup(<span>Saving…</span>, 8000, 'save');

    const limitReached = await checkPaletteLimit();
    if (limitReached) {
      addPopup(
        <span>You've reached the maximum limit of 250 saved palettes. Please delete some palettes before saving new ones.</span>,
        6000,
        'error'
      );
      return;
    }

    // Create a palette object that includes only the duo palette
    const paletteToSave = {
      name: `Secondary scale ${new Date().toLocaleString()}`,
      timestamp: Date.now(),
      palettes: {
        duo: {
          color: colorDuo,
          colorName: colorNameDuo,
          palette: paletteDuo,
          squareNames: squareNames,
          paletteModifiers: paletteModifiersDuo,
          baseGeneratedPalette: duoRef.current?.getBaseGeneratedPalette() || paletteDuo,
          selectedIndex: selectedIndexDuo,
          tailwindFamily: duoRef.current?.getTailwindFamily() || null
        }
      }
    };

    savePalette(paletteToSave);

    updatePopup(
      popupId,
      <span>
        Saved "{colorNameDuo}"
        <Link to="/saved">
          Go to Saved
        </Link>
      </span>,
      4000,
      'save'
    );
  };

  const handleSaveTrio = async () => {
    if (!user) {
      addPopup(
        <span>Please sign in to save palettes</span>,
        4000,
        'error'
      );
      return;
    }

    const popupId = addPopup(<span>Saving…</span>, 8000, 'save');

    const limitReached = await checkPaletteLimit();
    if (limitReached) {
      addPopup(
        <span>You've reached the maximum limit of 250 saved palettes. Please delete some palettes before saving new ones.</span>,
        6000,
        'error'
      );
      return;
    }
    // Create a palette object that includes only the trio palette
    const paletteToSave = {
      name: `Tertiary Scale ${new Date().toLocaleString()}`,
      timestamp: Date.now(),
      palettes: {
        trio: {
          color: colorTrio,
          colorName: colorNameTrio,
          palette: paletteTrio,
          squareNames: squareNames,
          paletteModifiers: paletteModifiersTrio,
          baseGeneratedPalette: trioRef.current?.getBaseGeneratedPalette() || paletteTrio,
          selectedIndex: selectedIndexTrio,
          tailwindFamily: trioRef.current?.getTailwindFamily() || null
        }
      }
    };

    savePalette(paletteToSave);

    updatePopup(
      popupId,
      <span>
        Saved "{colorNameTrio}"
        <Link to="/saved">
          Go to Saved
        </Link>
      </span>,
      4000,
      'save'
    );
  };



  // deletes
  const openDeleteConfirmationDuo = (index) => {
    setConfirmRemovePopupDuo(true);
  };

  const openDeleteConfirmationTrio = (index) => {
    setConfirmRemovePopupTrio(true);
  };

  const handleConfirmDeleteDuo = () => {
    setShowDuo(false);
    // If Duo is active and being removed, switch to Mono
    if (selectedOption === 'duo') {
      setSelectedOption('mono');
    }
    setPaletteDuo([]);
    setConfirmRemovePopupDuo(false);

    const newPath = encodeStateToURL({
      color,
      colorDuo: '',
      colorTrio,
      showDuo: false,
      showTrio,
      paletteModifiers,
      paletteModifiersDuo: {
        scale: { hue: 0, lightnessLeft: 0, lightnessRight: 0, saturationLeft: 0, saturationRight: 0 },
        individual: {}
      },
      paletteModifiersTrio,
      squareNames
    });
    updateURL(newPath, true);
  };

  const handleConfirmDeleteTrio = () => {
    setShowTrio(false);
    // If Trio is active and being removed, switch to another option
    if (selectedOption === 'trio') {
      setSelectedOption('duo');
    }
    setPaletteTrio([]);

    setConfirmRemovePopupTrio(false);

    const newPath = encodeStateToURL({
      color,
      colorDuo,
      colorTrio: '',
      showDuo,
      showTrio: false,
      paletteModifiers,
      paletteModifiersDuo,
      paletteModifiersTrio: {
        scale: { hue: 0, lightnessLeft: 0, lightnessRight: 0, saturationLeft: 0, saturationRight: 0 },
        individual: {}
      },
      squareNames
    });
    updateURL(newPath, true);
  };

  const handleCancelDeleteDuo = () => {
    setConfirmRemovePopupDuo(false);
  };

  const handleCancelDeleteTrio = () => {
    setConfirmRemovePopupTrio(false);
  };



  // nienke here: deactivate tailwind
  const handleColorizeMe = () => {
    // Generate new random colors for all palettes
    const newColor = generateRandomColor();
    const newColorDuo = generateRandomColorDuo();
    const newColorTrio = generateRandomColorTrio();

    // Update mono component through its ref
    if (monoRef.current) {
      // This calls the same function used by the "Colorize me" button inside Mono component
      monoRef.current.colorizeMe(newColor);
    }

    // Update duo component if it's shown
    if (showDuo && duoRef.current) {
      duoRef.current.colorizeMe(newColorDuo);
    }

    // Update trio component if it's shown
    if (showTrio && trioRef.current) {
      trioRef.current.colorizeMe(newColorTrio);
    }
  };

  const copyToClipboard = async (colorCode) => {
    try {
      const formattedColor = colorCode.startsWith('#') ? colorCode : `#${colorCode}`;

      await navigator.clipboard.writeText(formattedColor);
      addPopup(
        <span className="copied">
          Copied "{formattedColor}"
        </span>,
        4000,
        'copy'
      );

    } catch (err) {
      console.error('Failed to copy color to clipboard:', err);

      // Fallback for older browsers - define formattedColor here too
      const formattedColor = colorCode.startsWith('#') ? colorCode : `#${colorCode}`;
      const textArea = document.createElement('textarea');
      textArea.value = formattedColor;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  // prevent scroll on right content when in left content
  return (
    <div className={`home ${selectedOption}`}>

      <>
        {/* Export popup */}
        {isExportVisible && (
          <PopupDefault onClose={closeExport}>
            <Export
              palette={getActivePopupData().palette}
              colorName={getActivePopupData().colorName}
              onClose={closeExport}
            />
          </PopupDefault>
        )}

        {/* Export to Figma popup */}
        {isExportToFigmaVisible && (
          <PopupDefault onClose={closeExportToFigma}>
            <ExportToFigma
              palette={getActivePopupData().palette}
              colorName={getActivePopupData().colorName}
              selectedIndex={getActivePopupData().selectedIndex}
              squareNames={getActivePopupData().squareNames}
              onClose={closeExportToFigma}
            />
          </PopupDefault>
        )}

        {/* Contrast Grid popup */}
        {isContrastGridVisible && (
          <PopupDefault onClose={closeContrastGrid}>
            <ContrastGrid
              colors={getActivePopupData().palette.map((color, index) => ({
                name: getActivePopupData().squareNames[index],
                hex: color.startsWith('hsl') ? chroma(color).hex() : color
              }))}
              onClose={closeContrastGrid}
            />
          </PopupDefault>
        )}

        {/* share */}
        {isShareVisible && (
          <PopupDefault onClose={closeShare}>
            <CloseButton onClose={closeShare}/>
            <Share
              onClose={closeShare}
              palette={palette}
              paletteDuo={paletteDuo}
              paletteTrio={paletteTrio}
              showDuo={showDuo}
              showTrio={showTrio}
              colorName={colorName}
              colorNameDuo={colorNameDuo}
              colorNameTrio={colorNameTrio}
              squareNames={squareNames}
              paletteModifiers={paletteModifiers}
              paletteModifiersDuo={paletteModifiersDuo}
              paletteModifiersTrio={paletteModifiersTrio}
              color={color}
              colorDuo={colorDuo}
              colorTrio={colorTrio}
              selectedIndex={selectedIndex}
              selectedIndexDuo={selectedIndexDuo}
              selectedIndexTrio={selectedIndexTrio}
            />
          </PopupDefault>
        )}

        {/* duo */}
        {confirmRemovePopupDuo && (
          <PopupDefaultSmall onClose={handleCancelDeleteDuo}>
            <CloseButton onClose={handleCancelDeleteDuo} />
            <div className="popup-content">
              <p>Are you sure you want to delete this color scale?</p>
              <div className="btns-container">
                <button className="cancel-btn qua-btn btn-fixedW" onClick={handleCancelDeleteDuo}>
                  <span>Cancel</span>
                </button>
                <button className="confirm-delete-btn err-btn btn-fixedW" onClick={handleConfirmDeleteDuo}>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </PopupDefaultSmall>
        )}

        {/* trio */}
        {confirmRemovePopupTrio && (
          <PopupDefaultSmall onClose={handleCancelDeleteTrio}>
            <CloseButton onClose={handleCancelDeleteTrio} />
            <div className="popup-content">
              <p>Are you sure you want to delete this color scale?</p>
              <div className="btns-container">
                <button className="cancel-btn qua-btn btn-fixedW" onClick={handleCancelDeleteTrio}>
                  <span>Cancel</span>
                </button>
                <button className="confirm-delete-btn err-btn btn-fixedW" onClick={handleConfirmDeleteTrio}>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </PopupDefaultSmall>
        )}

        {/* NEW SINGLE POPUP */}
        {activePopups.length > 0 && (
          <div className="popup-timed-container">
            {activePopups.map((popup) => (
              <div
                key={popup.id}
                className={`popup-timed ${popup.type || 'default'}`}
                style={{ minHeight: 'auto' }} // Reset dimensions
              >
                <div className="popup-timed-content">
                  <div className="content">
                    {popup.type === 'error' ? (
                      // Error/warning icon
                      <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#b56600">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                      </svg>

                    ) : (
                      // Success/checkmark icon (default)
                      <svg className="check-icon-fill" width={24} height={24} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                      </svg>
                    )}
                    {popup.message}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="home-content-left">

          <div className="mobile-actions">
            <div className="generate-btn sec-btn"
              onClick={handleColorizeMe}>
              {/* <span className="btn-text">Generate random {showDuo ? 'palette' : 'scale'}</span> */}
              <span className="btn-text">Generate random color</span>
            </div>
            <button
              className={`toggle-dashboard-btn sec-btn ${viewDashboard ? 'show' : ''}`}
              onClick={() => setViewDashboard(!viewDashboard)}
            >
              <svg className="tools-icon" width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437 1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008Z" />
              </svg>

              <svg className="hide-icon" width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>


            </button>
          </div>

          {/* toggle dashboard button minified */}
          <button
            className={`toggle-dashboard-btn--minified ${viewDashboard ? 'show' : ''}`}
            onClick={() => setViewDashboard(!viewDashboard)}
          >
            <svg width={28} height={28} xmlns="http://www.w3.org/2000/svg" fill="#ffffff" viewBox="0 0 24 24" strokeWidth={1} stroke="#211f1c">
              <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          </button>

          {/* sidebar */}
          <div className={`sidebar ${viewDashboard ? 'show' : ''}`}>
            {isLoading ? (
              <Loader />
            ) : (
              <div>
                <div className="dashboard--container">

                  {/* mono component */}
                  <div className={`colorize-container--mono`}>
                    <Mono
                      ref={monoRef}
                      color={color || '#000000'}
                      setColor={setColor}
                      palette={palette}
                      setPalette={setPalette}
                      selectionMode='mono'
                      selectionName='PRIMARY'
                      selectedIndex={selectedIndex}
                      setSelectedIndex={setSelectedIndex}
                      editIndex={editIndex}
                      setEditIndex={setEditIndex}
                      paletteModifiers={paletteModifiers}
                      setPaletteModifiers={setPaletteModifiers}
                      squareNames={squareNames}
                      setSquareNames={setSquareNames}
                      setBaseColorName={setColorName}
                      onSave={handleSaveMono}
                      onOpenExport={() => openExport('mono')}
                      onOpenExportToFigma={() => openExportToFigma('mono')}
                      onOpenContrastGrid={() => openContrastGrid('mono')}
                      onColorChange={(newColor) => {
                        if (!isUpdatingFromURL) {
                          setColor(newColor);

                          // nienke nwe 8 mar
                          setTailwindFamily(null); // clear tailwind family when a new color is picked manually

                          const currentPath = window.location.pathname;
                          const colorInURL = newColor.replace('#', '').toLowerCase();
                          const colorAlreadyInURL = currentPath.toLowerCase().includes(colorInURL);
                          const hasTailwindInURL = currentPath.includes('&TW');

                          if (colorAlreadyInURL && hasTailwindInURL) {
                            return; // back navigation, skip
                          }
                       
                          const newPath = encodeStateToURL({
                            color: newColor,
                            colorDuo,
                            colorTrio,
                            showDuo,
                            showTrio,
                            paletteModifiers: {
                              scale: { hue: 0, lightnessLeft: 0, lightnessRight: 0, saturationLeft: 0, saturationRight: 0 },
                              individual: {}
                            }, // Reset modifiers when generating new color
                            paletteModifiersDuo,
                            paletteModifiersTrio,
                            squareNames,
                            tailwindFamily: null,
                            tailwindFamilyDuo,
                            tailwindFamilyTrio
                          });
                          updateURL(newPath, true);
                        }
                      }}
                      onModifiersChange={(newModifiers) => {
                        if (!isUpdatingFromURL) {
                          setPaletteModifiers(newModifiers);
                          const newPath = encodeStateToURL({
                            color,
                            colorDuo,
                            colorTrio,
                            showDuo,
                            showTrio,
                            paletteModifiers: newModifiers,
                            paletteModifiersDuo,
                            paletteModifiersTrio,
                            squareNames
                          });
                          updateURL(newPath, true);
                        }
                      }}
                      onTailwindFamilyChange={(newFamily, newColor) => {
                        if (!isUpdatingFromURL) {
                          setTailwindFamily(newFamily);
                          setColor(newColor);
                          const newPath = encodeStateToURL({
                            color: newColor,  // use newColor directly, not stale color state
                            colorDuo,
                            colorTrio,
                            showDuo,
                            showTrio,
                            paletteModifiers: {
                              scale: { hue: 0, lightnessLeft: 0, lightnessRight: 0, saturationLeft: 0, saturationRight: 0 },
                              individual: {}
                            },
                            paletteModifiersDuo,
                            paletteModifiersTrio,
                            squareNames,
                            tailwindFamily: newFamily,  // use newFamily directly, not stale state
                            tailwindFamilyDuo,
                            tailwindFamilyTrio
                          });
                          updateURL(newPath, true);
                        }
                      }}
                      isUpdatingFromURL={isUpdatingFromURL}
                    />
                  </div>

                  {/* add duo */}
                  {!showDuo && (
                    <button
                      className={`add-secondary-color-scale-btn sec-btn svg---text ${!subscriptionActive && 'guard'}`}
                      onClick={() => executeMembershipGuard(() => {
                        setShowDuo(true);
                        setSelectedOption('duo');
                        // Reset any duo-related state
                        const newColorDuo = generateRandomColorDuo();
                        setColorDuo(newColorDuo);
                        setPaletteDuo([]);
                        setPaletteModifiersDuo({
                          scale: {
                            hue: 0,
                            lightnessLeft: 0,
                            lightnessRight: 0,
                            saturationLeft: 0,
                            saturationRight: 0
                          },
                          individual: {}
                        });
                        setEditIndexDuo(null);
                        setSelectedIndexDuo(null);
                        // Increment the key to force a complete re-render
                        setDuoKey(prevKey => prevKey + 1);

                        const newPath = encodeStateToURL({
                          color,
                          colorDuo: newColorDuo,
                          colorTrio,
                          showDuo: true,
                          showTrio,
                          paletteModifiers,
                          paletteModifiersDuo: {
                            scale: {
                              hue: 0,
                              lightnessLeft: 0,
                              lightnessRight: 0,
                              saturationLeft: 0,
                              saturationRight: 0
                            },
                            individual: {}
                          },
                          paletteModifiersTrio,
                          squareNames
                        });
                        updateURL(newPath, true);
                      })}
                    >

                    {!isAuthReady ? (
                      <div className="skeleton-parent">
                        <SkeletonButton/>
                      </div>
                    ) : (
                      <>
                        <svg width={20} height={20} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        <span>Add secondary color scale</span>
                        {!subscriptionActive &&
                          <div className="guardTxt">PRO</div>
                        }
                      </>)}
                    </button>
                  )}

                  {/* duo component */}
                  <div className={`colorize-container--duo ${showDuo ? 'showSideBar' : 'hideSideBar'}`}>
                    <Duo
                      ref={duoRef}
                      key={duoKey}
                      color={colorDuo || '#000000'}
                      setColor={setColorDuo}
                      palette={paletteDuo}
                      setPalette={setPaletteDuo}
                      selectionMode='duo'
                      selectionName='SECONDARY'
                      selectedIndex={selectedIndexDuo}
                      setSelectedIndex={setSelectedIndexDuo}
                      editIndex={editIndexDuo}
                      setEditIndex={setEditIndexDuo}
                      paletteModifiers={paletteModifiersDuo}
                      setPaletteModifiers={setPaletteModifiersDuo}
                      squareNames={squareNames}
                      setSquareNames={setSquareNames}
                      setBaseColorName={setColorNameDuo}
                      onSave={handleSaveDuo}
                      onOpenExport={() => openExport('duo')}
                      onOpenExportToFigma={() => openExportToFigma('duo')}
                      onOpenContrastGrid={() => openContrastGrid('duo')}
                      //
                      onColorChange={(newColor) => {
                        if (!isUpdatingFromURL) {
                          setColorDuo(newColor);
                          const newPath = encodeStateToURL({
                            color,
                            colorDuo: newColor,
                            colorTrio,
                            showDuo,
                            showTrio,
                            paletteModifiers,
                            paletteModifiersDuo: {
                              scale: { hue: 0, lightnessLeft: 0, lightnessRight: 0, saturationLeft: 0, saturationRight: 0 },
                              individual: {}
                            }, // Reset modifiers when generating new color
                            paletteModifiersTrio,
                            squareNames
                          });
                          updateURL(newPath, true);
                        }
                      }}
                      onModifiersChange={(newModifiers) => {
                        if (!isUpdatingFromURL) {
                          setPaletteModifiersDuo(newModifiers);
                          // Update URL with new modifiers
                          const newPath = encodeStateToURL({
                            color,
                            colorDuo,
                            colorTrio,
                            showDuo,
                            showTrio,
                            paletteModifiers,
                            paletteModifiersDuo: newModifiers,
                            paletteModifiersTrio,
                            squareNames
                          });
                          updateURL(newPath, true);
                        }
                      }}
                      onTailwindFamilyChange={(newFamily) => {
                        if (!isUpdatingFromURL) {
                          setTailwindFamily(newFamily);
                          const newPath = encodeStateToURL({
                            color,
                            colorDuo,
                            colorTrio,
                            showDuo,
                            showTrio,
                            paletteModifiers,
                            paletteModifiersDuo,
                            paletteModifiersTrio,
                            squareNames,
                            tailwindFamily: newFamily,
                            tailwindFamilyDuo,
                            tailwindFamilyTrio
                          });
                          updateURL(newPath, true);
                        }
                      }}
                      isUpdatingFromURL={isUpdatingFromURL}
                    />

                    {/* delete duo - minified */}
                    {showDuo && !showTrio && (
                      <button
                        className="delete-secondary-color-scale-btn--minified"
                        title="Delete secondary color scale"
                        onClick={() => {
                          openDeleteConfirmationDuo();
                        }}
                      >
                        <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#211f1c">
                          <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* add trio */}
                  {showDuo && !showTrio && (
                    <button
                      className="add-tertiary-color-scale-btn sec-btn svg---text"
                      onClick={() => executeMembershipGuard(() => {
                        setShowTrio(true);
                        setSelectedOption('trio');
                        // Reset any duo-related state
                        const newColorTrio = generateRandomColorTrio();
                        setColorTrio(newColorTrio);
                        setPaletteTrio([]);
                        setPaletteModifiersTrio({
                          scale: {
                            hue: 0,
                            lightnessLeft: 0,
                            lightnessRight: 0,
                            saturationLeft: 0,
                            saturationRight: 0
                          },
                          individual: {}
                        });
                        setEditIndexTrio(null);
                        setSelectedIndexTrio(null);
                        // Increment the key to force a complete re-render
                        setTrioKey(prevKey => prevKey + 1);

                        const newPath = encodeStateToURL({
                          color,
                          colorDuo,
                          colorTrio: newColorTrio,
                          showDuo,
                          showTrio: true,
                          paletteModifiers,
                          paletteModifiersDuo,
                          paletteModifiersTrio: {
                            scale: {
                              hue: 0,
                              lightnessLeft: 0,
                              lightnessRight: 0,
                              saturationLeft: 0,
                              saturationRight: 0
                            },
                            individual: {}
                          },
                          squareNames
                        });
                        updateURL(newPath, true);
                      })}
                    >
                    {!isAuthReady ? (
                      <div className="skeleton-parent">
                        <SkeletonButton />
                      </div>
                    ) : (
                      <>
                        <svg width={20} height={20} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        <span>Add tertiary color scale</span>
                      </>)}
                    </button>
                  )}

                  {/* delete duo */}
                  {showDuo && !showTrio && (
                    <button
                      className="delete-secondary-color-scale-btn ter-btn svg---text"
                      onClick={() => {
                        openDeleteConfirmationDuo();
                      }}
                    >
                      <svg width={22} height={22} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#211f1c">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                      </svg>
                      <span>Delete secondary color scale</span>
                    </button>
                  )}

                  {/* trio component */}
                  <div className={`colorize-container--trio ${showTrio ? 'showSideBar' : 'hideSideBar'}`}>
                    <Trio
                      ref={trioRef}
                      key={trioKey}
                      color={colorTrio || '#000000'}
                      setColor={setColorTrio}
                      palette={paletteTrio}
                      setPalette={setPaletteTrio}
                      selectionMode='trio'
                      selectionName='TERTIARY'
                      selectedIndex={selectedIndexTrio}
                      setSelectedIndex={setSelectedIndexTrio}
                      editIndex={editIndexTrio}
                      setEditIndex={setEditIndexTrio}
                      paletteModifiers={paletteModifiersTrio}
                      setPaletteModifiers={setPaletteModifiersTrio}
                      squareNames={squareNames}
                      setSquareNames={setSquareNames}
                      setBaseColorName={setColorNameTrio}
                      onSave={handleSaveTrio}
                      onOpenExport={() => openExport('trio')}
                      onOpenExportToFigma={() => openExportToFigma('trio')}
                      onOpenContrastGrid={() => openContrastGrid('trio')}
                      onColorChange={(newColor) => {
                        if (!isUpdatingFromURL) {
                          setColorTrio(newColor);

                          const newPath = encodeStateToURL({
                            color,
                            colorDuo,
                            colorTrio: newColor,
                            showDuo,
                            showTrio,
                            paletteModifiers,
                            paletteModifiersDuo,
                            paletteModifiersTrio: {
                              scale: { hue: 0, lightnessLeft: 0, lightnessRight: 0, saturationLeft: 0, saturationRight: 0 },
                              individual: {}
                            }, // Reset modifiers when generating new color
                            squareNames
                          });
                          updateURL(newPath, true);
                        }
                      }}
                      onModifiersChange={(newModifiers) => {
                        if (!isUpdatingFromURL) {
                          setPaletteModifiersTrio(newModifiers);
                          // Update URL with new modifiers
                          const newPath = encodeStateToURL({
                            color,
                            colorDuo,
                            colorTrio,
                            showDuo,
                            showTrio,
                            paletteModifiers,
                            paletteModifiersDuo,
                            paletteModifiersTrio: newModifiers,
                            squareNames
                          });
                          updateURL(newPath, true);
                        }
                      }}
                      onTailwindFamilyChange={(newFamily) => {
                        if (!isUpdatingFromURL) {
                          setTailwindFamily(newFamily);
                          const newPath = encodeStateToURL({
                            color,
                            colorDuo,
                            colorTrio,
                            showDuo,
                            showTrio,
                            paletteModifiers,
                            paletteModifiersDuo,
                            paletteModifiersTrio,
                            squareNames,
                            tailwindFamily: newFamily,
                            tailwindFamilyDuo,
                            tailwindFamilyTrio
                          });
                          updateURL(newPath, true);
                        }
                      }}
                      isUpdatingFromURL={isUpdatingFromURL}
                    />

                    {/* delete trio - minified */}
                    {showTrio && (
                      <button
                        className="delete-tertiary-color-scale-btn--minified"
                        title="Delete tertiary color scale"
                        onClick={() => {
                          openDeleteConfirmationTrio();
                        }}
                      >
                        <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#211f1c">
                          <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* delete trio */}
                  {showTrio && (
                    <button
                      className="delete-tertiary-color-scale-btn svg---text ter-btn"
                      onClick={() => {
                        openDeleteConfirmationTrio();
                      }}
                    >
                      <svg width={22} height={22} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#211f1c">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                      </svg>
                      <span>Delete tertiary color scale</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>


        <div className="home-content-right">
          {/* {isLoading ? (
            <Loader />
          ) : (
            <div> */}

              <Header
                // titleHighlighted="Color starts here"
                headingLarge="Color starts here"
                headingHome={
                  <>
                    More than a color generator. No61 lets you build and fine-tune Tailwind CSS color scales, then test them against more than 40+ real UI components, cards, dashboards, products, the works.
                    <br /><br />
                    Check WCAG accessibility scores on the spot, share palettes with a link, or export to Tailwind 3, Tailwind 4, SCSS, CSS, or Figma.
                    <br /><br />
                    Your palette, stress-tested.
                  </>
                }
                // bodyText={
                //   <>
                //     More than a color generator. No61 lets you build and fine-tune Tailwind CSS color scales, then test them against more than 40+ real UI components, cards, dashboards, products, the works.
                //     <br /><br />
                //     Check WCAG accessibility scores on the spot, share palettes with a link, or export to Tailwind 3, Tailwind 4, SCSS, CSS, or Figma.
                //     <br /><br />
                //     Your palette, stress-tested.
                //   </>
                // }
              >
              </Header>

              <div className="fake-show-squares--container" ref={colorizeContainerRef}>
                <div
                  className={`show-squares--container ${showSquares ? "active--container" : ""} ${showDuo ? 'duo' : ''} ${showTrio ? 'trio' : ''}`}
                  ref={innerColorizeRef}
                >
                  <div className="squares">
                    
                    <div className="palette-actions">
                      {showDuo &&
                        <div className={`action generate-btn svg---text`}
                          onClick={handleColorizeMe}>
                          <svg width={22} height={22} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#211f1c">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.098 19.902a3.75 3.75 0 0 0 5.304 0l6.401-6.402M6.75 21A3.75 3.75 0 0 1 3 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 0 0 3.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008Z" />
                          </svg>
                          <span className="btn-text">Generate random palette</span>
                        </div>
                      }
                      {showDuo &&
                        <button className="action save-btn svg---text" onClick={handleSave}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                            <path d="M19 8.81818C19 6.70946 17.1345 5 14.8333 5C13.1128 5 11.6358 5.95563 11 7.31927C10.3642 5.95563 8.88722 5 7.16667 5C4.86548 5 3 6.70946 3 8.81818C3 14.9447 11 19 11 19C11 19 19 14.9447 19 8.81818Z" stroke="#211f1c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>Save palette</span>
                        </button>
                        // nienke
                      }
                      {!isAuthReady ? (
                        <div className="svg---text">
                          <SkeletonButton width="36"/>
                        </div>
                      ) : (
                        <button className={`action share-btn svg---text ${!subscriptionActive && 'guard'}`} onClick={() => executeMembershipGuard(openShare)}>
                        <svg width={21} height={21} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                        </svg>
                        <span>Share</span>
                        {!subscriptionActive &&
                          <div className="guardTxt">PRO</div>
                        }
                      </button>
                      )}
                    </div>
                    

                    {/* <p className="color-name">{colorName}</p> */}
                    <div className="squares--container">
                      {palette.map((shade, idx) => (
                        <div
                          key={idx}
                          className={`square-container ${selectedIndex === idx ? 'picked-color' : ''} ${editIndex === idx ? 'edit-indiv' : ''}`}
                        >
                          <div
                            className="square"
                            style={{
                              backgroundColor: palette[idx],
                              color: getContrastRatio(palette[idx], '#ffffff') > 2.0
                                ? '#ffffff' : '#211f1c'
                            }}
                            onClick={() => copyToClipboard(shade.startsWith('hsl') ? chroma(shade).hex() : shade)}
                          >
                            {selectedIndex === idx && (
                              <svg className="flag-icon" xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 17 17" fill="none">
                                <ellipse cx="5.66667" cy="15.2292" rx="5.66667" ry="1.77083" fill="currentColor" />
                                <path d="M16.2923 1.41667H6.37565V0H4.95898V14.1667H6.37565V8.5H16.2923L14.8757 4.95833L16.2923 1.41667ZM12.0423 4.95833C12.0423 5.7375 11.4048 6.375 10.6257 6.375C9.84648 6.375 9.20898 5.7375 9.20898 4.95833C9.20898 4.17917 9.84648 3.54167 10.6257 3.54167C11.4048 3.54167 12.0423 4.17917 12.0423 4.95833Z" fill="currentColor" />
                              </svg>
                            )}
                            <p className="variable">{squareNames[idx]}</p>
                            <p className="color-code">
                              {(shade.startsWith('hsl') ? chroma(shade).hex() : shade).replace('#', '')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {showDuo &&
                    <div className="squares">
                      <p className="color-name">{colorNameDuo}</p>
                      <div className="squares--container">
                        {paletteDuo.map((shade, idx) => (
                          <div
                            key={idx}
                            className={`square-container ${selectedIndexDuo === idx ? 'picked-color' : ''} ${editIndexDuo === idx ? 'edit-indiv' : ''}`}
                          >
                            <div
                              className="square"
                              style={{
                                backgroundColor: paletteDuo[idx],
                                color: getContrastRatio(paletteDuo[idx], '#ffffff') > 2.0
                                  ? '#ffffff' : '#211f1c'
                              }}
                              onClick={() => copyToClipboard(shade.startsWith('hsl') ? chroma(shade).hex() : shade)}
                            >
                              {selectedIndexDuo === idx && (
                                <svg className="flag-icon" xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 17 17" fill="none">
                                  <ellipse cx="5.66667" cy="15.2292" rx="5.66667" ry="1.77083" fill="currentColor" />
                                  <path d="M16.2923 1.41667H6.37565V0H4.95898V14.1667H6.37565V8.5H16.2923L14.8757 4.95833L16.2923 1.41667ZM12.0423 4.95833C12.0423 5.7375 11.4048 6.375 10.6257 6.375C9.84648 6.375 9.20898 5.7375 9.20898 4.95833C9.20898 4.17917 9.84648 3.54167 10.6257 3.54167C11.4048 3.54167 12.0423 4.17917 12.0423 4.95833Z" fill="currentColor" />
                                </svg>
                              )}
                              <p className="variable">{squareNames[idx]}</p>
                              <p className="color-code">
                                {(shade.startsWith('hsl') ? chroma(shade).hex() : shade).replace('#', '')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  }

                  {showTrio &&
                    <div className="squares">
                      <p className="color-name">{colorNameTrio}</p>
                      <div className="squares--container">
                        {paletteTrio.map((shade, idx) => (
                          <div
                            key={idx}
                            className={`square-container ${selectedIndexTrio === idx ? 'picked-color' : ''} ${editIndexTrio === idx ? 'edit-indiv' : ''}`}
                          >
                            <div
                              className="square"
                              style={{
                                backgroundColor: paletteTrio[idx],
                                color: getContrastRatio(paletteTrio[idx], '#ffffff') > 2.0
                                  ? '#ffffff' : '#211f1c'
                              }}
                              onClick={() => copyToClipboard(shade.startsWith('hsl') ? chroma(shade).hex() : shade)}
                            >
                              {selectedIndexTrio === idx && (
                                <svg className="flag-icon" xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 17 17" fill="none">
                                  <ellipse cx="5.66667" cy="15.2292" rx="5.66667" ry="1.77083" fill="currentColor" />
                                  <path d="M16.2923 1.41667H6.37565V0H4.95898V14.1667H6.37565V8.5H16.2923L14.8757 4.95833L16.2923 1.41667ZM12.0423 4.95833C12.0423 5.7375 11.4048 6.375 10.6257 6.375C9.84648 6.375 9.20898 5.7375 9.20898 4.95833C9.20898 4.17917 9.84648 3.54167 10.6257 3.54167C11.4048 3.54167 12.0423 4.17917 12.0423 4.95833Z" fill="currentColor" />
                                </svg>
                              )}
                              <p className="variable">{squareNames[idx]}</p>
                              <p className="color-code">
                                {(shade.startsWith('hsl') ? chroma(shade).hex() : shade).replace('#', '')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  }
                  <button
                    onClick={() => setShowSquares((prev) => !prev)}
                    className={`show-squares-btn ${isOutOfView || showSquares ? "active--handler" : ""}`}
                  >
                    <svg width={36} height={36} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#ffffff">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m9 12.75 3 3m0 0 3-3m-3 3v-7.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                  </button>
                </div>
              </div>

              <Modules
                colors={palette}
                colorsDuo={paletteDuo}
                colorsTrio={paletteTrio}
                selectedOption={selectedOption}
                showDuo={showDuo}
                showTrio={showTrio}
                darkMode={darkMode}
              />
            {/* </div>
          )} */}
        </div>
      </>



    </div>
  );
}

export default Home;